import React, { useState, useEffect } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  getColor,
  Icons,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import utilsCommon from "../../shared/utils/common";
import { t } from "i18next";
import utilsDocs from "../../shared/utils/docs";
import { handleDocPreview, handleDocDownload } from "../../shared/utils/api";
import { useNavigate } from "react-router-dom";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import WorkgroupAddModal from "../../components/WorkgroupAddModal";
import ProfileModal from "../../components/HeaderUserDropdown/ProfileModal";
import { ActionType } from "../../types/workgroup";
import useAppStore from "../../redux/dispatcher/useAppStore";
import { nanoid } from "@reduxjs/toolkit";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";

const Suggestion = () => {
  /** Hook 정리 */
  const toast = useToast();
  const navigate = useNavigate();
  const { appState } = useAppStore();
  const { getContentDownload } = useDocumentStore();
  const { workgroupState, getMyDashboardSuggestion } = useWorkgroupStore();

  /** State 정리 */
  const [randomNum, changeRandomNum] = useState(4);
  const [openExtraModal, changeOpenExtraModal] = useState("CLOSE");
  const [tasks, changeTasks] = useState([]) as any[];
  const [validTaskIdx, changeValidIdx] = useState<number[] | undefined>();

  /** useEffect 정리 */
  useEffect(() => {
    changeTasks(
      workgroupState?.suggestions?.tasks as {
        type: ActionType;
        data: any;
      }[]
    );
    const newTasks = workgroupState?.suggestions?.tasks;
    const validIdx = [4];
    if (
      newTasks?.filter((item: any) => item.type === "recentDocument")?.length >
      0
    )
      validIdx.push(0);
    if (
      newTasks?.filter((item: any) => item.type === "managedWorkgroup")
        ?.length > 0
    )
      validIdx.push(1);
    if (!appState.appRegisterStatus.zoom || !appState.appRegisterStatus.dropbox)
      validIdx.push(2);
    if (newTasks?.filter((item: any) => item.type === "sendFile")?.length > 0)
      validIdx.push(3);
    changeValidIdx(validIdx);
  }, [workgroupState?.suggestions?.tasks, appState.appRegisterStatus]);
  useEffect(() => {
    if (validTaskIdx === undefined) return;
    if (validTaskIdx?.length === 0) {
      getMyDashboardSuggestion();
      return;
    }
    changeRandomNum(
      validTaskIdx[Math.floor(Math.random() * validTaskIdx?.length)]
    );
  }, [validTaskIdx]);

  /** 함수 정리 */
  const handleReadNorDownloadContent = (task: any) => {
    const { data } = task.filter(
      (item: any) => item.type === "recentDocument"
    )[0];
    if (
      utilsCommon.checkLicenseAvailable("allowWebView") &&
      utilsCommon.checkExtensionAvailable(data.contentName, "fwocExtensions")
    ) {
      handleDocPreview({
        permission: "view",
        workgroupId: data.workgroupId,
        syncId: data.syncId,
        toast,
        t,
        revisionNo: 0,
        noNeedToast: true,
      }).then((resp) => {
        if (resp !== "SUCCESS") {
          handleDocDownload(
            data.syncId,
            getContentDownload,
            toast,
            t,
            "0",
            "",
            true
          ).then((resp1) => {
            if (resp1) {
              toast.toastMsg(
                nanoid(),
                t("home.myTasks.alert.noViewSoDownload"),
                "success"
              );
            } else {
              toast.toastMsg(nanoid(), t("home.myTasks.alert.noView"), "error");
            }
          });
        }
      });
    } else {
      handleDocDownload(data.syncId, getContentDownload, toast, t);
    }
  };
  const getButtonProps = (randomNum: number) => {
    return {
      label:
        {
          0:
            tasks &&
            tasks[0] &&
            utilsCommon.checkMenuAvailble("preview", tasks[0]?.data)
              ? t("home.myTasks.preview")
              : t("file.action.download"),
          1: t("home.suggestion.inviteMember"),
          2: t("home.suggestion.appIntegration"),
          3: t("home.suggestion.goMyBox"),
          4: t("home.suggestion.setNotification"),
        }[randomNum] || "",
      icon:
        {
          0:
            tasks &&
            tasks[0] &&
            utilsCommon.checkMenuAvailble("preview", tasks[0]?.data)
              ? "preview"
              : "download",
          1: "addPerson",
          2: "plusCircle",
          3: "send",
          4: "noti",
        }[randomNum] || "",
      onClick:
        {
          0: () => {
            handleReadNorDownloadContent(tasks);
            changeValidIdx(validTaskIdx?.filter((item) => item !== 0));
          },
          1: () => {
            changeOpenExtraModal("ADD_NEW_MEMBER");
            changeValidIdx(validTaskIdx?.filter((item) => item !== 1));
          },
          2: () => {
            changeOpenExtraModal("SETTING_APP");
            changeValidIdx(validTaskIdx?.filter((item) => item !== 2));
          },
          3: () => navigate(`${process.env.PUBLIC_URL}/myInbox/sent`),
          4: () => {
            changeOpenExtraModal("SETTING_ALARM");
            changeValidIdx(validTaskIdx?.filter((item) => item !== 4));
          },
        }[randomNum] || undefined,
    };
  };
  const { label, icon, onClick } = getButtonProps(randomNum);

  /** UI: 추천활동 - 내용*/
  const getRecommendationContent = (randomNum: number) => {
    const recentDoc = tasks?.filter(
      (item: any) => item.type === "recentDocument"
    )[0]?.data;
    const sendFile = tasks?.filter((item: any) => item.type === "sendFile")[0]
      ?.data;
    const managedWorkgroup = tasks?.filter(
      (item: any) => item.type === "managedWorkgroup"
    )[0]?.data;
    switch (randomNum) {
      case 0:
      case 1:
      case 3:
        return (
          <>
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {
                {
                  0: t("home.suggestion.recentDocUpdate"),
                  1: t("home.suggestion.inviteColleague"),
                  3: t("home.suggestion.recentSendFile"),
                }[randomNum]
              }
            </Typography>
            <Box classes="mg mt-16 home-borderBox--content">
              {randomNum === 1 ? (
                <Box direction="column" alignItems="center" width={"100%"}>
                  <Avatar
                    uniqueCode={managedWorkgroup?.workgroupId}
                    shape="workgroup"
                    size="48"
                    name={managedWorkgroup?.workgroupName}
                  />
                  <Tooltip message={managedWorkgroup?.workgroupName}>
                    <Typography classes="mg mt-8" type="body2_rg" ellipsis>
                      {managedWorkgroup?.workgroupName}
                    </Typography>
                  </Tooltip>
                </Box>
              ) : (
                <Box direction="column" alignItems="center" width={"100%"}>
                  {utilsDocs.renderDocIcon(
                    randomNum === 0
                      ? recentDoc?.contentName
                      : sendFile?.contentName,
                    40
                  )}
                  <Tooltip
                    message={
                      randomNum === 0
                        ? recentDoc?.contentName
                        : sendFile?.contentName
                    }
                  >
                    <Typography classes="mg mt-8" type="body2_rg" ellipsis>
                      {randomNum === 0
                        ? recentDoc?.contentName
                        : sendFile?.contentName}
                    </Typography>
                  </Tooltip>
                  <Tooltip
                    message={
                      randomNum === 0
                        ? utilsCommon.renderProperWorkgroupName(
                            recentDoc?.workgroup?.workgroupId,
                            recentDoc?.workgroup?.workgroupName
                          )
                        : utilsCommon.renderProperWorkgroupName(
                            sendFile?.workgroup?.workgroupId,
                            sendFile?.workgroup?.workgroupName
                          )
                    }
                  >
                    <Typography
                      type="body4_rg"
                      ellipsis
                      exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    >
                      {randomNum === 0
                        ? utilsCommon.renderProperWorkgroupName(
                            recentDoc?.workgroup?.workgroupId,
                            recentDoc?.workgroup?.workgroupName
                          )
                        : utilsCommon.renderProperWorkgroupName(
                            sendFile?.workgroup?.workgroupId,
                            sendFile?.workgroup?.workgroupName
                          )}
                    </Typography>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </>
        );

      case 2:
        return (
          <Box direction="column" alignItems="center" height="100%">
            <Box maxHeight={88} height={"100%"} alignItems="center">
              {["zoom", "dropbox"].map((name, idx) => (
                <Box
                  key={name}
                  width={48}
                  height={48}
                  alignItems="center"
                  justifyContent="center"
                  classes={idx > 0 ? "mg ml-12" : undefined}
                >
                  <img
                    src={require(`../../shared/images/common/${name}.png`)}
                    alt="thumbnail"
                    width={37}
                    height={37}
                    style={name === "dropbox" ? { borderRadius: "3px" } : {}}
                  />
                </Box>
              ))}
            </Box>
            <Typography type="subtitle2" textAlign="center" classes="mg mt-8">
              {t("home.suggestion.appTitle")}
            </Typography>
            <Typography
              type="body3_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              whiteSpace="normal"
              wordBreak="keep-all"
              classes="mg mt-4"
              maxWidth={235}
              textAlign="center"
            >
              {t("home.suggestion.appDesc")}
            </Typography>
          </Box>
        );

      default:
        return (
          <Box
            direction="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <img
              src={require("../../shared/images/common/alarm.png")}
              alt="thumbnail"
              // width={166}
              height={"100%"}
              style={{ maxHeight: 100 }}
            />
            <Typography type="subtitle2" textAlign="center">
              {t("home.suggestion.alarmTitle")}
            </Typography>
            <Typography
              type="body3_rg"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
              whiteSpace="normal"
              wordBreak="keep-all"
              maxWidth={235}
              textAlign="center"
            >
              {t("home.suggestion.alarmDesc")}
            </Typography>
          </Box>
        );
    }
  };
  return (
    <Box classes="home-borderBox">
      {[
        "CREATE_NEW_WORKGROUP",
        "SHARE_TO_WORKGROUP",
        "SEND_FILE",
        "ADD_NEW_MEMBER",
      ].includes(openExtraModal) && (
        <WorkgroupAddModal
          createType={
            openExtraModal === "SHARE_TO_WORKGROUP"
              ? "SHARE_TO_WORKGROUP"
              : openExtraModal === "CREATE_NEW_WORKGROUP"
              ? "CREATE_NEW_WORKGROUP"
              : openExtraModal === "ADD_NEW_MEMBER"
              ? "ADD_NEW_MEMBER"
              : "SEND_FILE"
          }
          open
          workgroupInfo={
            openExtraModal === "ADD_NEW_MEMBER"
              ? workgroupState.suggestions?.tasks?.filter(
                  (item: any) => item.type === "managedWorkgroup"
                )[0]?.data
              : undefined
          }
          onClose={() => changeOpenExtraModal("CLOSE")}
        />
      )}
      {["SETTING_APP", "SETTING_ALARM"].includes(openExtraModal) && (
        <ProfileModal
          open
          onClose={() => changeOpenExtraModal("CLOSE")}
          startTab={openExtraModal === "SETTING_APP" ? "APP" : "ALARM"}
        />
      )}

      <Typography type="subtitle1" classes="mg mb-12">
        {t("home.suggestion.title")}
      </Typography>
      <Box height={216} direction="column">
        {getRecommendationContent(randomNum)}
        <Button
          btnStyle="fill"
          width="fit-content"
          label={label}
          size="lg"
          style={{ alignSelf: "center", marginTop: "14px" }}
          leadingIcon={<Icons variant="line" label={icon} isOn />}
          onClick={onClick}
        />
      </Box>
    </Box>
  );
};

export default Suggestion;
